import {
  Icon,
  IconButton,
  Stack,
  TableHeadProps,
  TableRow,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { ApplicationShort } from '@schooly/api';
import { DATE_FORMAT } from '@schooly/constants';
import {
  CancelIcon,
  ConvertedIcon,
  GridCell,
  GridHead,
  InformationIcon,
  Note2Icon,
  TagSelect,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { formatApplicationDate, getAge } from '@schooly/utils/application-helpers';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getNationalitiesLabelIds } from '@schooly/utils/get-nationalities-label-ids';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { FC, ReactElement, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

export const SKELETON_COLS = 6;
const CHILD_ROW_HEIGHT = 30;
const TAG_CLASS_NAME = 'ApplicationsGrid__tag';
export const ApplicationsHeader: FC<TableHeadProps & { rightIcon?: ReactNode }> = ({
  rightIcon,
  ...rest
}) => {
  const { formatMessage } = useIntl();

  return (
    <GridHead borderBottom {...rest}>
      <GridCell width="230px">{formatMessage({ id: 'applications-Applicant' })}</GridCell>
      <GridCell width="110px">{formatMessage({ id: 'applications-StartingDate' })}</GridCell>
      <GridCell width="230px">{formatMessage({ id: 'userType-child-plural' })}</GridCell>
      <GridCell width="160px">
        {formatMessage({ id: 'date-of-birthday' })}{' '}
        <GridTooltip title={formatMessage({ id: 'applications-AgeCalculating' })}>
          <IconButton inverse>
            <InformationIcon />
          </IconButton>
        </GridTooltip>
      </GridCell>

      <GridCell width="100px">{formatMessage({ id: 'peopleDetail-Nationality' })}</GridCell>
      <GridCell>{formatMessage({ id: 'applications-AdmissionNotes' })}</GridCell>
    </GridHead>
  );
};

export const HOVER_ICON_CLASS_NAME = 'hoverIcon';

const GridTooltip: FC<{ title: ReactNode; children: ReactElement<any, any> }> = ({
  children,
  title,
}) => {
  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            boxShadow: `0px 20px 40px rgba(37, 39, 90, 0.1)`,
            backgroundColor: theme.palette.background.paper,
            '& .MuiTooltip-arrow': {
              color: theme.palette.background.paper,
            },
          }),
        },
      }}
      arrow
      title={title}
    >
      {children}
    </Tooltip>
  );
};

const noteTooltipProps: Omit<TooltipProps, 'title' | 'children'> = {
  componentsProps: {
    tooltip: {
      sx: (theme) => ({
        zIndex: 2000,
        maxWidth: 400,
        boxShadow: `0px 20px 40px rgba(37, 39, 90, 0.1)`,
        backgroundColor: theme.palette.background.paper,
        '& .MuiTooltip-arrow': {
          color: theme.palette.background.paper,
        },
      }),
    },
  },
  placement: 'bottom',
  arrow: true,
};

type ApplicationRowProps = {
  application: ApplicationShort;
};

export const ApplicationRow: FC<ApplicationRowProps> = ({ application }) => {
  const { formatMessage } = useIntl();
  const rejected = application.status === 'rejected';
  const converted = application.status === 'converted';
  const parent = application.adults.find((p) => Boolean(p.primary));
  const { children } = application;
  if (!parent) {
    return null;
  }

  const stackProps = {
    height: CHILD_ROW_HEIGHT,
    justifyContent: 'center',
  };

  return (
    <TableRow
      component={Link}
      to={`/applications/${application.id}`}
      sx={(theme) => {
        let iconColor = theme.palette.common.grey;
        let backgroundColor: string | undefined = theme.palette.background.default;

        if (converted) {
          iconColor = theme.palette.success.main;
          backgroundColor = theme.palette.success.superLight;
        }
        if (rejected) {
          iconColor = theme.palette.error.main;
          backgroundColor = theme.palette.error.superLight;
        }

        return {
          '&:hover td': {
            '& .MuiTypography-root': {
              color: theme.palette.primary.main,
            },
            backgroundColor,
            [`& .${TAG_CLASS_NAME}`]: {
              backgroundColor: theme.palette.background.paper,
            },
            [`& .${HOVER_ICON_CLASS_NAME}`]: {
              color: iconColor,
            },
          },
        };
      }}
      data-test-id="application-table-row"
    >
      <GridCell py={1.5} pl={2} style={{ verticalAlign: 'top' }}>
        <Stack
          {...stackProps}
          gap={1}
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          {rejected && (
            <GridTooltip
              title={
                <Typography color="common.grey2">
                  <FormattedMessage id="applications-Status-Rejected" />
                </Typography>
              }
            >
              <Icon
                className={HOVER_ICON_CLASS_NAME}
                sx={(theme) => ({
                  color: theme.palette.common.grey,
                })}
              >
                <CancelIcon />
              </Icon>
            </GridTooltip>
          )}

          {converted && (
            <GridTooltip
              title={
                <Typography color="common.grey2">
                  <FormattedMessage id="applications-Status-Converted" />
                </Typography>
              }
            >
              <Icon
                className={HOVER_ICON_CLASS_NAME}
                sx={(theme) => ({
                  color: theme.palette.common.grey,
                })}
              >
                <ConvertedIcon />
              </Icon>
            </GridTooltip>
          )}
          <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
            {getUserFullName(parent)}
          </TypographyWithOverflowHint>
        </Stack>
      </GridCell>
      <GridCell py={1.5}>
        {children.map((child) => (
          <Stack key={child.id} {...stackProps}>
            <Typography color="text.primary">
              {formatApplicationDate(child.preferred_start_date)}
            </Typography>
          </Stack>
        ))}
      </GridCell>
      <GridCell py={1.5}>
        {children.map((child) => (
          <Stack key={child.id} {...stackProps}>
            <Typography variant="h3" color="text.primary">
              {getUserFullName(child)}
            </Typography>
          </Stack>
        ))}
      </GridCell>
      <GridCell py={1.5}>
        {children.map((child) => (
          <Stack
            {...stackProps}
            key={child.id}
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            gap={1}
          >
            <Typography color="text.primary">
              {format(newDateTimezoneOffset(child.date_of_birth), DATE_FORMAT)}
            </Typography>
            <TagSelect
              className={TAG_CLASS_NAME}
              size="small"
              sx={(theme) => ({
                '&.MuiChip-root': {
                  color: theme.palette.text.primary,
                },
              })}
              label={`${getAge(child.date_of_birth, child.preferred_start_date)} y.o`}
            />
          </Stack>
        ))}
      </GridCell>
      <GridCell py={1.5}>
        {children.map((child) => (
          <Stack key={child.id} {...stackProps}>
            <Typography color="text.primary">
              {getNationalitiesLabelIds(child.nationalities)
                .map((id) => formatMessage({ id }))
                .join(', ')}
            </Typography>
          </Stack>
        ))}
      </GridCell>
      <GridCell py={1.5}>
        {children.map((child) => (
          <Stack
            key={child.id}
            {...stackProps}
            flexDirection="row"
            alignItems="center"
            position="relative"
            justifyContent="flex-start"
            gap={1}
          >
            {child.notes ? (
              <>
                <Icon
                  sx={(theme) => ({
                    position: 'absolute',
                    pointerEvents: 'none',
                    color: theme.palette.common.grey,
                  })}
                >
                  <Note2Icon />
                </Icon>
                <TypographyWithOverflowHint
                  pl={3.25}
                  color="text.primary"
                  tooltipProps={noteTooltipProps}
                >
                  {child.notes}
                </TypographyWithOverflowHint>
              </>
            ) : (
              <Typography color="text.secondary">— </Typography>
            )}
          </Stack>
        ))}
      </GridCell>
    </TableRow>
  );
};
